import { createScheduleComponent } from '@play-co/components';
import type { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import { duration } from '../utils/duration';

/** Schedule initialization boundary; the first weekly window starts August 7, 2026. */
export const dreamDriftScheduleInitDate = '2026-07-31';

/** Weekly three-day Dream Drift event schedule in the game's configured timezone. */
export const dreamDriftSchedule = createScheduleComponent(
  dreamDriftScheduleInitDate,
  'Asia/Tokyo',
  {
    weekly: [{ day: 'Friday', time: '00:00', duration: { days: 3 } }],
  },
);

/** Match-2 level at which Dream Drift becomes available. */
export const dreamDriftUnlockLevel = 40;

/** Maximum duration of one Dream Drift instance. */
export const dreamDriftInstanceDuration = duration({ hours: 3 });

/** Remaining instance time at which the Dream Meter becomes urgent. */
export const dreamDriftUrgentDuration = duration({ minutes: 15 });

/** Hero powers available for Dream Drift instances. */
export const dreamDriftHeroes = [
  'rumbleHero',
  'whirlwindDancer',
  'skySummoner',
  'prismPainter',
] as const;

/** Identifier for a selectable Dream Drift hero power. */
export type DreamDriftHero = (typeof dreamDriftHeroes)[number];

/** Friday event that anchors the first theme in the weekly rotation, in Japan. */
export const dreamDriftThemeRotationStart = '2026-09-18T00:00:00+09:00';

/** Playable themes in rotation order; append newly implemented themes here. */
export const dreamDriftThemes = ['asiatravel', 'momotaro', 'urashima'] as const;

/** Identifier for a Dream Drift event theme. */
export type DreamDriftTheme = (typeof dreamDriftThemes)[number];

/** Terminal results that can produce a Dream Drift recap. */
export const dreamDriftInstanceResults = ['completed', 'expired'] as const;

/** Terminal result for a Dream Drift instance. */
export type DreamDriftInstanceResult =
  (typeof dreamDriftInstanceResults)[number];

/** Approved source Match-2 levels by difficulty, with retained levels before new ones in each band. */
export const dreamDriftLevels = {
  easy: [
    199, 4001, 245, 263, 947, 979, 327, 153, 281, 231, 169, 4949, 157, 186, 202,
    142,
  ],
  medium: [
    386, 1897, 1965, 1863, 4255, 196, 236, 771, 267, 373, 4002, 335, 244, 309,
    246, 178, 1154, 219, 502, 478, 210, 259, 681, 4963, 1115, 156, 214, 503,
    217, 4904, 103, 545, 609, 135, 4926, 4941, 4995, 192, 293, 4959,
  ],
  hard: [
    1012, 4083, 3954, 4401, 976, 2719, 306, 2775, 4251, 131, 4479, 146, 338,
    4556, 4920, 605, 4220, 2766, 1599, 337, 2356, 2442, 2534, 4406, 4226, 1076,
    732, 1912, 4966, 144, 4207, 4918, 188, 110, 4932, 4912, 1539, 4914, 174,
    814,
  ],
} as const;

/** Difficulty band for each one-based Dream Drift node; milestone nodes four and eight are breathers. */
export const dreamDriftNodeBands = [
  'easy',
  'medium',
  'hard',
  'easy',
  'medium',
  'medium',
  'hard',
  'medium',
  'hard',
  'medium',
  'hard',
  'hard',
] as const satisfies readonly (keyof typeof dreamDriftLevels)[];

/** One reward-bearing progression milestone in the Dream Drift path. */
export type DreamDriftMilestone = {
  /** Dream Drift level associated with the milestone reward. */
  level: number;
  /** Rewards accumulated at this milestone for later settlement. */
  rewards: Match2Reward[];
};

/** Ordered twelve-node milestones that define Dream Drift progression and deferred rewards. */
export const dreamDriftMilestones: DreamDriftMilestone[] = [
  {
    level: 4,
    rewards: [{ type: 'gems', amount: 50 }],
  },
  {
    level: 8,
    rewards: [
      { type: 'gems', amount: 100 },
      { type: 'boosters', subType: 'roulette', amount: 1 },
    ],
  },
  {
    level: 12,
    rewards: [
      { type: 'gems', amount: 500 },
      { type: 'boosters', subType: 'dart', amount: 1 },
      { type: 'boosters', subType: 'bullet', amount: 1 },
    ],
  },
];
